import moment from 'moment-timezone';

import type { AiAction } from '~/components/AiChat/types';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import { formatLocalDayLabel, formatLocalTimeWithSeconds } from '~/DateTimeUtils';
import type { ClaimActionHistoryResponseClaimActionVersion } from '~/server_shared/openapi/data-contracts';

import type { ActionState } from '../types';
import {
  actionStateLabel,
  actionTitle,
  getNotifiedActionId,
  getReferencedActionId,
  isActionEntry,
  isHandlerTurn,
} from '../utils';

export type ActionVersion = ClaimActionHistoryResponseClaimActionVersion;

/** One read of an action's history for the log's entries about it. */
export interface ActionVersionsRead {
  /** Newest first, as the route pages them. */
  versions: ActionVersion[];
  /**
   * The timestamp of the newest entry about the action when the read was sent. claims-ai commits a
   * version before it publishes the entry about it, so the read holds the version of every entry up to
   * this one, and possibly not of a later one.
   */
  coversThrough: string;
}

/** One recorded version of the action, as the history lists it. */
export interface VersionRow {
  versionId: string;
  time: string;
  /** The version's own title. None where the version recorded no payload. */
  title?: string;
  /** The action as it stood at this version, which its explanation and values are read off. */
  action: AiAction | null;
  /** The recommendation as Clive first raised it. */
  isOriginal: boolean;
  /** The action's current version. */
  isNewest: boolean;
}

/** Where the action got to: the history's last row. */
export interface CurrentStateRow {
  label: string;
  /** When it was settled. A waiting action has no transition to time. */
  time?: string;
  /** The note it was settled with. */
  reason?: string;
}

/** Whether Clive's recommendation changed after it was raised, which is when its cards say so. */
export const hasRevisions = (action: AiAction): boolean => (action.history_count ?? 0) > 0;

/**
 * The action as it stood at one version.
 *
 * Narrowed as the tab narrows the actions list's rows: a version's `task_data` is the same payload the
 * row carries, which the generated contract types as a plain record on both.
 */
const asVersionAction = (action: AiAction, version: ActionVersion): AiAction | null =>
  version.task_data ? { ...action, task_data: version.task_data as AiAction['task_data'] } : null;

/** The instant a timestamp names, one without an offset read as UTC as the server writes it; NaN where it names none. */
export const instantOf = (timestamp: string): number => moment.utc(timestamp, moment.ISO_8601).valueOf();

/** The newest version recorded at or before an entry, where the read holds it. */
const versionAsLogged = (read: ActionVersionsRead, timestamp: string): ActionVersion | undefined => {
  const loggedAt = instantOf(timestamp);
  // NaN on either side compares false, so a time that does not parse matches nothing.
  if (!(loggedAt <= instantOf(read.coversThrough))) return undefined;

  return read.versions.find(({ recorded_at: recordedAt }) => instantOf(recordedAt) <= loggedAt);
};

/**
 * The action as it stood when an entry about it was logged: the newest version recorded at or before the
 * entry.
 *
 * `undefined` where the read cannot say, so the card falls back to what the entry said rather than to
 * another version's values: the read may not hold the entry's version yet, it holds none that old, that
 * version recorded no payload, or a time does not parse.
 */
export const actionAsLogged = (action: AiAction, read: ActionVersionsRead, timestamp: string): AiAction | undefined => {
  const version = versionAsLogged(read, timestamp);

  return version ? (asVersionAction(action, version) ?? undefined) : undefined;
};

/**
 * Whether an entry was about the action's current version: a later entry about the same version (it was
 * proposed, then decided) revised nothing. Only a read that covers the action's last entry, `lastTimestamp`,
 * can say its newest version is the current one.
 */
export const isCurrentVersion = (read: ActionVersionsRead, timestamp: string, lastTimestamp: string): boolean =>
  instantOf(read.coversThrough) >= instantOf(lastTimestamp) &&
  read.versions.length > 0 &&
  versionAsLogged(read, timestamp) === read.versions[0];

/**
 * The entries a revision superseded, each with the entry that superseded it.
 *
 * The card of an action Clive revised is its last action entry, in the order given. Every action entry
 * and handler turn about it before that one was about a version since replaced. Not every entry says
 * which revision it was about, so the version is matched by time (`actionAsLogged`). An action never
 * revised, or whose row has not been read, supersedes nothing.
 */
export const getSupersededEntries = (
  notifications: StpNotification[],
  actions: AiAction[]
): Map<StpNotification, StpNotification> => {
  const revisedIds = new Set(actions.filter(hasRevisions).map(({ action_id: actionId }) => actionId));
  const lastEntryIndex = new Map<string, number>();
  notifications.forEach((notification, index) => {
    const actionId = getNotifiedActionId(notification);
    if (actionId && revisedIds.has(actionId)) lastEntryIndex.set(actionId, index);
  });

  const superseded = new Map<StpNotification, StpNotification>();
  notifications.forEach((notification, index) => {
    if (!isActionEntry(notification) && !isHandlerTurn(notification)) return;

    const actionId = getReferencedActionId(notification);
    const last = actionId ? lastEntryIndex.get(actionId) : undefined;
    if (last !== undefined && index < last) superseded.set(notification, notifications[last]);
  });

  return superseded;
};

/**
 * Times to the second, from first to last, with the day named on the first and wherever it changes:
 * versions can span days, and a time alone would then read as though the history ran backwards.
 */
const timeline = (instants: (string | undefined)[]): (string | undefined)[] => {
  let previousDay: string | undefined;

  return instants.map((instant) => {
    if (!instant) return undefined;

    const day = formatLocalDayLabel(instant);
    const time = formatLocalTimeWithSeconds(instant);
    const isNewDay = day !== previousDay;
    previousDay = day;

    return isNewDay ? `${day} · ${time}` : time;
  });
};

/**
 * The history's rows: every version held, oldest first, then the state the action is in now.
 *
 * `newestFirst` is in the order the route pages it. Every action has a version from when it was
 * raised, so the oldest one held is the original unless an older page is still to be read.
 *
 * The current state is timed and quoted off `status_change` only once the row says the action is
 * settled: the transition can outlive a reopening, and a waiting action has none that is current.
 */
export const toHistoryRows = (
  action: AiAction,
  state: ActionState,
  newestFirst: ActionVersion[],
  hasMore: boolean
): { versions: VersionRow[]; current: CurrentStateRow } => {
  const oldestFirst = [...newestFirst].reverse();
  const settled = action.is_executed || action.is_ignored ? action.status_change : null;
  const times = timeline([...oldestFirst.map(({ recorded_at: recordedAt }) => recordedAt), settled?.at]);

  const versions = oldestFirst.map((version, index): VersionRow => {
    const versionAction = asVersionAction(action, version);

    return {
      versionId: version.version_id,
      time: times[index] ?? '',
      title: versionAction ? actionTitle(versionAction) : undefined,
      action: versionAction,
      isOriginal: index === 0 && !hasMore,
      isNewest: index === oldestFirst.length - 1,
    };
  });

  return {
    versions,
    current: {
      label: actionStateLabel(state, action),
      time: times.at(-1),
      reason: settled?.reason ?? undefined,
    },
  };
};
